<template>
  <div
    :key="`${subscription}-${loggedIn}`"
    class="relative mx-auto min-h-[500px] max-w-content-container"
  >
    <div v-if="loading" class="absolute left-0 w-full lg:top-1/2">
      <TheLoader />
    </div>

    <div v-else>
      <div
        v-if="isAuthorized"
        class="mb-6 lg:absolute lg:right-0 lg:-mt-[62px]"
      >
        <ButtonCommon
          v-if="reports.length"
          text="Download all"
          class="!w-full whitespace-nowrap !text-sm lg:!w-auto"
          event-id="button-download-all"
        >
          <IconDownload class="z-index w-full min-w-[16px] max-w-[16px]" />
        </ButtonCommon>
      </div>

      <div v-if="!reports.length" class="text-center text-xl">
        There are currently no reports published, please check back later
      </div>

      <div
        v-if="reports.length"
        class="grid grid-cols-1 items-center justify-center gap-4 md:grid-cols-2"
      >
        <div v-for="(report, index) in reports" :key="index">
          <SharedMarketVideoCard
            v-if="report?.tags[0]?.name === 'esg-video'"
            :report="report"
            :initial-fields="initialFields"
            :product="market"
            :src-image="report.images.desktop"
          />
          <SharedMarketReportCard
            v-else
            :report="report"
            :category="categoryReport"
            :initial-fields="initialFields"
            :src-image="report.images.desktop"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import SharedMarketReportCard from '~/components/shared/market/ReportCard.vue'
import ButtonCommon from '~/components/shared/common/Button.vue'
import SharedMarketVideoCard from '~/components/shared/market/VideoCard.vue'
import TheLoader from '~/components/TheLoader.vue'

import { ESG } from '~/constants/services/constants'

import IconDownload from '~/assets/icons/icon-download.svg'
import { logError } from '~/utils/log-error'
import { MARKET_TYPE } from '~/src/constants/markets'
import { REPORT_TAG_VIDEO_INTRO } from '~/src/constants/reports'
import useAuthentication from '~/composables/useAuthentication'
import { useAuthorization } from '~/composables/useAuthorization'
import type { TReport, TReportCard } from '~/src/types/reports'
import { setReportImages } from '~/src/utils/reports/image'
import type { TMarketSlug } from '~/src/types/markets'

const { hasAuthorized } = useAuthorization()
const { loggedIn } = useAuthentication()

const props = defineProps<{
  category: string
  categoryReport: string
  categoryReportAuxiliary?: string
  market: TMarketSlug
  subscription: string
}>()

const { $downloadManager } = useNuxtApp()
const loading = ref(true)
const files = ref<TReport[]>([]) as Ref<TReport[]>
const reports = ref<TReportCard[]>([])

const initialFields = computed(() => {
  return [props.market, ESG.slug]
})
const isAuthorized = ref(false)

type TReportFiltered = {
  match: TReport[]
  noMatch: TReport[]
}

const filterReports = (reports: TReport[], search: string): TReportFiltered => {
  return reports.reduce(
    (result, item) => {
      const hasMatchSearch = item.tags.some((tag) => tag.name === search)
      if (hasMatchSearch) {
        result.match.push(item)
      } else {
        result.noMatch.push(item)
      }
      return result
    },
    { match: [], noMatch: [] },
  )
}

const orderFiles = () => {
  // get index from esg-report file post-type
  const indexEsgReportFile = files.value.noMatch.findIndex(
    (file) => file.tags[0]?.name === 'esg-report',
  )

  // remove a file from array and get the file to later put in the first position
  const esgReportFile = files.value.noMatch.splice(indexEsgReportFile, 1)[0]

  if (esgReportFile) {
    files.value.noMatch.unshift(esgReportFile)
  }
  reports.value = files.value.noMatch.map((report) => ({
    ...report,
  }))
}
const getReports = async () => {
  loading.value = true
  try {
    const data = await $downloadManager.getAllReports({
      market: props.market,
      marketTypes: [MARKET_TYPE.SUSTAINABILITY],
      categories: props.categoryReportAuxiliary
        ? [props.categoryReport, props.categoryReportAuxiliary]
        : [props.categoryReport],
      show_total: true,
      page_limit: -1,
    })

    files.value = filterReports(data, REPORT_TAG_VIDEO_INTRO.SUSTAINABILITY)
    orderFiles()
    reports.value = setReportImages(reports.value, props.market)
  } catch (error) {
    logError(error)
  } finally {
    loading.value = false
  }
}

watch(loggedIn, async (value) => {
  if (value) {
    isAuthorized.value = await hasAuthorized(props.subscription)
  }
})

onMounted(async () => {
  await getReports()
  if (loggedIn.value) {
    isAuthorized.value = await hasAuthorized(props.subscription)
  }
})
</script>
